import { AuthTokens } from '../types/auth.types';
import { UserInfo } from '../types/jwt.types';

export type AuthMethod = 'direct' | 'device';

export interface StoredAuthData {
  tokens: AuthTokens;
  userInfo: UserInfo;
  authMethod: AuthMethod;
  timestamp: number;
}

export class TokenStorageService {
  private static readonly TOKENS_KEY = 'auth_tokens';
  private static readonly USER_INFO_KEY = 'user_info';
  private static readonly AUTH_METHOD_KEY = 'auth_method';
  private static readonly TIMESTAMP_KEY = 'auth_timestamp';

  static storeAuthData(tokens: AuthTokens, userInfo: UserInfo, authMethod: AuthMethod): void {
    try {
      const timestamp = Date.now();
      
      localStorage.setItem(this.TOKENS_KEY, JSON.stringify(tokens));
      localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(userInfo));
      localStorage.setItem(this.AUTH_METHOD_KEY, authMethod);
      localStorage.setItem(this.TIMESTAMP_KEY, timestamp.toString());
    } catch (error) {
      console.error('Failed to store authentication data:', error);
      throw new Error('Failed to store authentication data');
    }
  }

  static getStoredTokens(): AuthTokens | null {
    try {
      const tokensJson = localStorage.getItem(this.TOKENS_KEY);
      return tokensJson ? JSON.parse(tokensJson) : null;
    } catch (error) {
      console.error('Failed to retrieve stored tokens:', error);
      return null;
    }
  }

  static getStoredUserInfo(): UserInfo | null {
    try {
      const userInfoJson = localStorage.getItem(this.USER_INFO_KEY);
      return userInfoJson ? JSON.parse(userInfoJson) : null;
    } catch (error) {
      console.error('Failed to retrieve stored user info:', error);
      return null;
    }
  }

  static getAuthMethod(): AuthMethod | null {
    return localStorage.getItem(this.AUTH_METHOD_KEY) as AuthMethod | null;
  }

  static getAuthTimestamp(): number | null {
    const timestamp = localStorage.getItem(this.TIMESTAMP_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  }

  static getStoredAuthData(): StoredAuthData | null {
    const tokens = this.getStoredTokens();
    const userInfo = this.getStoredUserInfo();
    const authMethod = this.getAuthMethod();
    const timestamp = this.getAuthTimestamp();

    if (!tokens || !userInfo || !authMethod || !timestamp) {
      return null;
    }

    return {
      tokens,
      userInfo,
      authMethod,
      timestamp
    };
  }

  static clearAuthData(): void {
    try {
      localStorage.removeItem(this.TOKENS_KEY);
      localStorage.removeItem(this.USER_INFO_KEY);
      localStorage.removeItem(this.AUTH_METHOD_KEY);
      localStorage.removeItem(this.TIMESTAMP_KEY);
      
      // Also clear any legacy keys that might exist
      localStorage.removeItem('auth_tokens');
      localStorage.removeItem('user_info');
      localStorage.removeItem('auth_method');
    } catch (error) {
      console.error('Failed to clear authentication data:', error);
    }
  }

  static isAuthDataValid(): boolean {
    const authData = this.getStoredAuthData();
    if (!authData) {
      return false;
    }

    // Check if authentication is not too old (7 days)
    const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    const isNotExpired = Date.now() - authData.timestamp < maxAge;

    return isNotExpired;
  }

  static getValidAuthData(): StoredAuthData | null {
    if (!this.isAuthDataValid()) {
      this.clearAuthData();
      return null;
    }

    return this.getStoredAuthData();
  }
}